import {useState, useEffect} from "react";
import useFetch from 'use-http';
import PageHeading from "../components/Base/Elements/Nav/PageHeading";
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import InputBox from "../components/Base/Elements/Input/InputBox";
import TunnelConnect from "../components/Tunnels/TunnelConnect";
import Toggle from "../components/Tunnels/Elements/TunnelTypeToggle";
import ProductionRequiredAlert from "../components/Base/Elements/Alerts/ProductionRequiredAlert";
import EnvironmentDropdown from "../components/Base/Elements/Nav/EnvironmentDropdown";
import ConfirmModal from "../components/Base/Elements/Confirmations/ConfirmModal";
import MissingFieldsAlert from "../components/Base/Elements/Alerts/MissingFieldsAlert";
import logo from '../../images/retrohook.png';

export default function Tunnels(props : any) {
  const { post, response, loading } = useFetch();
  const form = {
    PK: props.user.id,
    deviceName: "",
    bgp: false,
    deviceNamePlaceholder: String.raw `Your MLLP HL7 Server`,
    type: String.raw `ipsec.1`,
    LocalIpv4AddressPlaceholder: String.raw `Your Server's Private IPv4 Address`,
    LocalIpv4Address: String.raw ``,
    PublicIpv4Address: String.raw ``,
    PublicIpv4AddressPlaceholder: String.raw `Your Server's Public IPv4 Address`,
    RemoteIpv4Address: String.raw `172.31.0.0`,
    ikeVersion: String.raw `ikev2`,
    owner: props.user,
  }
  const [ikeToggle, setIkeToggle] = useState('');
  const [showEnvironmentDropdown, setShowEnvironmentDropdown] = useState(false);
  const [tunnels, setTunnels] = useState([]);
  const [updatedConnectClickHandler, setUpdatedConnectClickHandler] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [showAccessAlert, setShowAccessAlert] = useState(false);
  const [tunnel, setTunnel] = useState(form);
  const [missingFieldsAlert, setMissingFieldsAlert] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [currentTunnel, setCurrentTunnel] = useState({ name: 'New Tunnel' });
  const [isModalOpen, setIsModalOpen] = useState(false);


  useEffect(() => { getTunnels(); }, [props.selectedEnvironment, props.selectedAccount]);

  const getTunnels = async () => {
    setShowAccessAlert(false);
    if (props?.selectedEnvironment?.name?.substring(3, 6) == 'Dev') {
      setShowAccessAlert(true);
    }
    const body = { selectedWorkspace: props.selectedWorkspace, selectedEnvironment: props.selectedEnvironment };
    const tunnels: any = await post(`ipsec/list/${props.user.attributes.id}`, body);
    if (response.ok && tunnels.length > 0) {
      tunnels.map(async (element : any) => {
        if (!element.showDownloadTunnelConfig || element.showDownloadTunnelConfig === "undefined") {
          element.showDownloadTunnelConfig = false;
          element.showPsk = false;
        }
      });
      setTunnels(tunnels ? tunnels : []);
    } else {
      setTunnels([]);
    }
  }
  const canCreate = () => {
    const hasUpdateWorkflowInSelectedWorkspace = props.selectedWorkspace.accounts.filter((workspace : any) => {
      if (workspace.permissions?.tunnels.create) return workspace;
    });
    const isOwner = props.selectedWorkspace?.PK == props.account?.PK ? true : false;
    if (props.admin === true || hasUpdateWorkflowInSelectedWorkspace.length || isOwner) {  return true; } else { return false; }
  }
  const canDelete = () => {
    const hasUpdateWorkflowInSelectedWorkspace = props.selectedWorkspace.accounts.filter((workspace : any) => {
      if (workspace.permissions?.tunnels.delete) 
        return workspace;
    });
    const isOwner = props.selectedWorkspace.owner.email === props.account.owner.email;
    if (props.admin === true || hasUpdateWorkflowInSelectedWorkspace.length || isOwner) {
      return true;
    } else {
      return false;
    }
  }
  const saveTunnel = async () => {
    if (props?.selectedEnvironment?.name.substring(3, 6) == 'Dev') {
      setShowAccessAlert(true);
    }
    if (!tunnel.deviceName || !tunnel.PublicIpv4Address || !tunnel.LocalIpv4Address) {
      const missingFields = [];
      if(!tunnel.deviceName) missingFields.push("Device Name");
      if(!tunnel.PublicIpv4Address) missingFields.push("Public IP Address");
      if(!tunnel.LocalIpv4Address) missingFields.push("Local IP Address");
      setMissingFields(missingFields as []);
      setMissingFieldsAlert(true);
      return;
    }
    if (canCreate()) {
      const newTunnel = {
        DeviceName: tunnel.deviceName,
        StaticRoutesOnly: !tunnel.bgp,
        PublicIpv4Address: tunnel.PublicIpv4Address,
        RemoteIpv4Address: tunnel.RemoteIpv4Address,
        LocalIpv4Address: tunnel.LocalIpv4Address,
        Type: tunnel.type,
        owner: props.user,
        BgpAsn: 65000
      };
      const body = {
        tunnel: newTunnel,
        selectedAccount: props.selectedAccount,
        selectedEnvironment: props.selectedEnvironment
      };
      const tunnelResponse = await post(`ipsec/create/${props.user.attributes.id}`, body);
      if (response.ok && tunnelResponse) {
        await getTunnels();
        setTunnel({
          PK: props.user.id,
          deviceName: "",
          bgp: false,
          deviceNamePlaceholder: String.raw `Your MLLP HL7 Server`,
          type: String.raw `ipsec.1`,
          LocalIpv4AddressPlaceholder: String.raw `Your Server's Private IPv4 Address`,
          LocalIpv4Address: String.raw ``,
          PublicIpv4Address: String.raw ``,
          PublicIpv4AddressPlaceholder: String.raw `Your Server's Public IPv4 Address`,
          RemoteIpv4Address: String.raw `172.31.0.0`,
          ikeVersion: String.raw `ikev2`,
          owner: props.user
        });
      }
    }
  }
  const deleteTunnel = async (tunnel : any) => {
    if (canDelete()) {
      const body = {
        selectedEnvironment: props.selectedEnvironment,
        selectedAccount: props.selectedAccount,
        tunnel
      };
      await post(`ipsec/delete/${props.user.attributes.id}`, body);
      if (response.ok) {
        getTunnels();
      }
    }
  }
  const toggleIkeVersion = () => {
    setIkeToggle(tunnel.ikeVersion === "IKEV1" ? "IKEV2" : "IKEV1");
  }
  const handleIPChange = (event : any) => {
    const updated = {
      ...tunnel,
      PublicIpv4Address: event.target.value
    };
    setTunnel(updated);
  }
  const handleTunnelNameChange = (event : any) => {
    const updated = {
      ...tunnel,
      deviceName: event.target.value
    };
    setTunnel(updated);
  }
  const handleInternalIPChange = (event : any) => {
    const updated = {
      ...tunnel,
      LocalIpv4Address: event.target.value
    };
    setTunnel(updated);
  }
  const toggleBGP = (event : any) => {
    const updated = {
      ...tunnel,
      bgp: event.target.value
    };
    setTunnel(updated);
  }
  const showDownloadTunnelConfigClickHandler = async (tunnel : any) => {
    let shouldReRender = false;
    tunnels.forEach((element : any) => {
      if (element.id === tunnel.id) {
        if (element.showDownloadTunnelConfig === 'undefined') {
          element.showDownloadTunnelConfig = false;
        }
        if (!element.showDownloadTunnelConfig || element.showDownloadTunnelConfig === false) {
          element.showDownloadTunnelConfig = true;
          shouldReRender = true;
        } else {
          element.showDownloadTunnelConfig = false;
        }
      }
    });
    setUpdatedConnectClickHandler(shouldReRender);
    setTunnels(tunnels);
  }
  const openModal = (tunnel: any) => {
    setCurrentTunnel(tunnel);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const confirmDelete = () => {
    deleteTunnel(currentTunnel);
    closeModal();
  };
  if (!props.selectedWorkspace || !props.selectedEnvironment) {
    return (
    <div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center">
                <h1 className="display-4">No Workspace Selected</h1>
                <p className="lead">Please select a workspace from the dropdown above.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  }
  return (
    <div className="min-h-full animate-color-change-2x">
      <div className="shadow-sm bg-gray-800 pr-2 min-w-0 md:flex md:items-center">
        <PageHeading className="inline" title="Tunnels"/>
        <div className="">
          {(props?.selectedWorkspace.environments.length > 0) &&
            <EnvironmentDropdown
              user={props.user}
              account={props.account} 
              admin={props.admin}
              setShowEnvironmentDropdown={setShowEnvironmentDropdown}
              environments={props.environments}
              selectedWorkspace={props.selectedWorkspace}
              updateBillingCustomer={props.updateBillingCustomer}
              selectedEnvironment={props.selectedEnvironment}
              selectedAccount={props.selectedAccount}
              setSelectedEnvironment={ props.setSelectedEnvironment }
              logout={props.logout}
            />
          }
        </div>
        { !loading && props?.selectedEnvironment?.name?.substring(1,3) != "Dev" && <button onClick={() => getTunnels() }
          className="ml-1 flex h-8 w-8 justify-end cursor-pointer py-0.5 text-gray-50 bg-cyan-400 rounded border-2 border-cyan-400 
          hover:bg-cyan-300 focus:outline-none">
          <ArrowPathIcon />
        </button>
        }
      </div>
      { showAccessAlert && ( <ProductionRequiredAlert closeProdRequiredAlert={ () => {  setShowAccessAlert(false); }}/>)}
      { missingFieldsAlert && missingFields.length > 0 && (<MissingFieldsAlert closeMissingFieldsAlert={ () => {  setMissingFieldsAlert(false); }} fields={missingFields}/>)}
      <ConfirmModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={() => confirmDelete()}
        title="Confirm Delete Tunnel"
        message={`Are you sure you want to delete ${currentTunnel.name}? This cannot be undone.`}
      />
      { loading && 
        <div className="items-center animate-pulse py-48 sm:py-24 md:py-32 lg:py-48">
          <img className="h-24 mx-auto w-auto" src={logo} alt="Logo" />
          <h2 className="mt-0 text-center text-2xl font-bold text-gray-200">
            Retrohook
            <span className="inline align-middle text-xs tracking-tight text-cyan-300 pl-1"></span>
          </h2>
        </div>
      }
      { !props.selectedWorkspace &&
        <div>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center">
                    <h1 className="display-4">No Workspace Selected</h1>
                    <p className="lead">Please select a workspace from the dropdown above.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      } 
      { !props.selectedEnvironment &&
        <div>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center">
                    <h1 className="display-4">No Environment Selected</h1>
                    <p className="lead">Please select a production enabled environment from the dropdown above.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      } 
      
      <dl className="p-4 grid grid-cols-4 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        { !loading && props.selectedWorkspace.PK &&
        <>
          <div className="relative bg-gray-800 pt-5 px-4 pb-2 sm:pt-6 sm:px-6 rounded-lg overflow-hidden border-gray-700 hover:border-cyan-300 border-2 border-dashed">
            <div className="cursor-pointer">
              <dt>
                <p className="text-center text-2xl font-semibold text-cyan-400 truncate">New Tunnel</p>
              </dt>
              <div className="divide-y divide-solid pt-8">
                <div className="py-4">
                  <InputBox name="Name" onChange={handleTunnelNameChange} defaultValue={ tunnel.deviceName } placeholder={ tunnel.deviceNamePlaceholder }/>
                </div>
                <div>
                  <div className="pb-2 pt-4">
                    <InputBox name="Public IPv4 Address" onChange={handleIPChange} defaultValue={ tunnel.PublicIpv4Address } placeholder={ tunnel.PublicIpv4AddressPlaceholder }/>
                  </div>
                  <div className="pb-2 pt-4">
                    <InputBox name="Private IPV4 Address" onChange={handleInternalIPChange} defaultValue={ tunnel.LocalIpv4Address } placeholder={ tunnel.LocalIpv4AddressPlaceholder }/>
                  </div>
                  {
                  advanced ? (
                    <div className="flex pt-2">
                      <div className="flex-auto">
                        <span className="text-xs">IKEv1&nbsp;&nbsp;</span>
                        <Toggle enabled={ikeToggle} name={ikeToggle} setEnabled={toggleIkeVersion} />
                        <span className="text-xs">&nbsp;&nbsp;IKEv2 Only</span>
                      </div>
                      <div className="flex-auto">
                        <span className="text-xs">Static&nbsp;&nbsp;</span>
                        <Toggle enabled={ tunnel.bgp } name={ tunnel.bgp } setEnabled={toggleBGP}/>
                        <span className="text-xs">&nbsp;&nbsp;Dynamic (BGP)</span>
                      </div>
                    </div>
                  ) : ("")
                } </div>
              </div>
            </div>
            <div className="pt-2 mt-2 pb-4 sm:px-6">
              {
              advanced ? (
                <button className="mt-2 w-full hover:bg-gray-500 bg-orange-500 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline" type="button"
                  onClick={
                    () => setAdvanced(!advanced)
                }>Standard</button>
              ) : (
                <button className="w-full bg-gray-500 hover:bg-orange-500 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline" type="button"
                  onClick={
                    () => setAdvanced(!advanced)
                }>Advanced</button>
              )
            }
              {
              canCreate() === true && (
                <button className="mt-2 w-full bg-cyan-300 hover:bg-cyan-400 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline" type="button"
                  onClick={saveTunnel}>Generate</button>
              )
            }
              {
              canCreate() === false && (
                <button className="mt-2 w-full bg-gray-500 hover:bg-cyan-100 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline" type="button"
                  onClick={
                    () => setShowAccessAlert(true)
                }>
                  {
                  loading ? "Generating..." : "Generate"
                }</button>
              )
            } </div>
          </div>
        </>
      }
      {!loading && tunnels?.map((tunnel : any) => {
        return (
          <div key={ tunnel.vpnConnectionId }
            className="relative bg-gray-800 pt-5 px-4 pb-4 sm:pt-6 sm:px-6 rounded-lg overflow-hidden hover:border-cyan-300 border-2 border-gray-700">
            { !tunnel.showDownloadTunnelConfig ? (
              <div className="cursor-pointer">
                <dt>
                  <div className="absolute rounded-md p-1">
                    {/* <LockClosedIcon className="h-8 w-8 text-gray-100 bg-cyan-300 rounded p-1 ring ring-cyan-400 ring-offset-2 ring-offset-gray-100"/> */}
                  </div>
                  <p className="text-center text-lg font-semibold text-gray-100 truncate">
                    {
                    tunnel.name ? tunnel.name.slice(0, 14) : tunnel.Tags[0].Value.slice(0, 14)
                  }</p>
                  <button className="absolute top-0 right-0 m-2 border-gray-500 border hover:bg-orange-500 hover:text-white hover:border-orange-500 text-gray-500 font-bold text-xs py-1 px-2 rounded focus:outline-none focus:shadow-outline"
                    onClick={
                      () => {
                        openModal(tunnel);
                      }
                  }>X</button>
                  <p className="text-center text-sm pb-2 text-cyan-500">
                    {
                    tunnel.state.toUpperCase()
                  }</p>
                  <p className="text-xs text-center text-gray-100">
                    {
                    tunnel.Tags[0].Value.slice(0, 8)
                  }...IPv4 (Left):{" "}
                    <span className=" text-gray-200 text-sm">
                      {
                      tunnel.customer_ip
                    }</span>
                  </p>
                  {
                  tunnel.Routes ? (
                    <p className="text-xs text-center text-gray-100">
                      { tunnel.Tags[0].Value.slice(0, 6) }...Subnet (Left):{" "}
                      <span className=" text-gray-200 text-sm">
                        { tunnel.Routes[0].DestinationCidrBlock }</span>
                    </p>
                  ) : null
                } </dt>
                <div className="divide-y divide-solid">
                  <div className="py-2">
                    <p className="text-xs text-cyan-400">Primary</p>
                    <p className="text-xs text-gray-100">Retrohook IPv4 (Right):{" "}
                      <span className=" text-gray-200 text-sm">
                        {" "}
                        {
                        tunnel.tunnel1_ip
                      }
                        {" "}</span>
                    </p>
                    {
                    tunnel.Options ? (
                      <p className="text-xs text-gray-100">Retrohook Subnet (Right):{" "}
                        <span className=" text-gray-200 text-sm">
                          {
                          tunnel.Options.RemoteIpv4NetworkCidr
                        }</span>
                      </p>
                    ) : null
                  }
                    <p className="text-xs text-gray-100">IKE:{" "}
                      <span className=" text-gray-200 text-sm">
                        {
                        tunnel.ike_auth_protocol + " | " + tunnel.ike_encryption_protocol + " | " + tunnel.ike_diffie_helm_group
                      }</span>
                    </p>
                    <p className="text-xs text-gray-100">IPSec:{" "}
                      <span className=" text-gray-200 text-sm">
                        {
                        tunnel.tunnel1_psk ? "psk" : ""
                      }
                        | {" "}
                        {
                        tunnel.ipsec_auth_protocol
                      }</span>
                    </p>
                    {
                    tunnel.showPsk ? (
                      <p className="text-xs text-white bg-red-800">PSK:{" "}
                        <span className="text-white text-xs">
                          {
                          tunnel.tunnel1_psk
                        }</span>
                      </p>
                    ) : ("")
                  } </div>
                  <div className="pb-5 pt-2">
                    <p className="text-xs text-cyan-400">Secondary</p>
                    <p className="text-xs text-gray-100">Retrohook IPv4 (Right):{" "}
                      <span className=" text-gray-200 text-sm">
                        {" "}
                        {
                        tunnel.tunnel2_ip
                      }
                        {" "}</span>
                    </p>
                    {
                    tunnel.Options ? (
                      <p className="text-xs text-gray-100">Retrohook Subnet (Right):{" "}
                        <span className=" text-gray-200 text-sm">
                          {
                          tunnel.Options.RemoteIpv4NetworkCidr
                        }</span>
                      </p>
                    ) : null
                  }
                    <p className="text-xs text-gray-100">
                      IKE:{" "}
                      <span className=" text-gray-200 text-sm">
                        {
                        tunnel.ike_auth_protocol + " | " + tunnel.ike_encryption_protocol + " | " + tunnel.ike_diffie_helm_group
                      }</span>
                    </p>
                    <p className="text-xs text-gray-100">
                      IPSec:{" "}
                      <span className=" text-gray-200 text-sm">
                        {
                        tunnel.tunnel2_psk ? "psk" : ""
                      }
                        |{" "}
                        {
                        tunnel.ipsec_auth_protocol
                      }</span>
                    </p>
                    {
                    tunnel.showPsk ? (
                      <p className="text-xs text-white bg-red-800">PSK:{" "}
                        <span className="text-white text-xs">
                          {
                          tunnel.tunnel2_psk
                        }</span>
                      </p>
                    ) : ("")
                  } </div>
                </div>
              </div>
              ) : ("")
            }
            { !tunnel.showDownloadTunnelConfig && 
            <button 
              className="my-2 w-full bg-gray-500 hover:bg-cyan-400 text-white font-bold py-2 px-4 rounded focus:outline-none  focus:shadow-outline" type="button" 
              onClick={() => { showDownloadTunnelConfigClickHandler(tunnel)}}
              >
              Get Connected
            </button>
            }
            { tunnel.showDownloadTunnelConfig && 
              <TunnelConnect tunnel={tunnel}
                user={ props.user }
                accounts={ props.accounts }
                account={ props.account }
                environments={ props.environments }
                selectedEnvironment={ props.selectedEnvironment }
                selectedAccount={ props.selectedAccount}
                showDownloadTunnelConfigClickHandler={showDownloadTunnelConfigClickHandler}
                logout={ props.logout }
              />
            } 
          </div>
          );
        })
      } 
      </dl>
    </div>
  );
}
